import React from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { RootState } from "../../store/Index";
import muzukurulogo from "../../assets/muzukurublack.png";

const SECTIONS: { title: string; body: React.ReactNode }[] = [
  {
    title: "1. Introduction",
    body: (
      <>
         Muzukuru Streaming Services (&quot;Muzukuru&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) provides
        funeral and event streaming, booking, and related coordination
        services through this platform. This Privacy Policy explains what
        personal information we collect, how we use it, and the choices you
        have. By using our services, you agree to the collection and use of
        information as described here.
      </>
    ),
  },
  {
    title: "2. Information We Collect",
    body: (
      <>
        We collect information you provide directly to us and information
        generated through your use of the platform, including:
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li>
            Account details — name, email address, phone number, and role
            (e.g. client, crew member, company representative).
          </li>
          <li>
            Booking and event details — names of the deceased or honorees,
            venue information, service dates, and related event content
            submitted for a booking.
          </li>
          <li>
            Media — livestream recordings, uploaded photos, memorial content,
            and comments submitted through the platform.
          </li>
          <li>
            Payment information — processed through our third-party payment
            providers; we do not store full card numbers on our servers.
          </li>
          <li>
            Technical data — IP address, device and browser type, and usage
            logs collected automatically for security and reliability.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "3. How We Use Your Information",
    body: (
      <>
        We use the information we collect to:
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li>Provide, operate, and maintain booking and streaming services.</li>
          <li>Process payments and send booking or service confirmations.</li>
          <li>
            Communicate with you about your bookings, including via email,
             SMS, or WhatsApp where you&apos;ve opted in.
          </li>
          <li>Improve platform performance, security, and reliability.</li>
          <li>Comply with legal obligations and enforce our terms of service.</li>
        </Box>
      </>
    ),
  },
  {
    title: "4. Sharing of Information",
    body: (
      <>
        We do not sell personal information. We may share information with:
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li>
            Service providers who help us operate the platform (e.g. hosting,
            payment processing, messaging providers), bound by confidentiality
            obligations.
          </li>
          <li>
            Funeral homes, companies, or crew members directly involved in
             fulfilling a booking you&apos;ve made or been assigned to.
          </li>
          <li>
            Authorities where required by law, or to protect the rights,
            safety, or property of Muzukuru or others.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "5. Livestreamed and Memorial Content",
    body: (
      <>
        Content shared during a livestream or published to a memorial page
        (including photos, comments, and tributes) may be visible to invited
        guests or the public, depending on the privacy setting chosen by the
        client who created the booking. Please consider what you share
        accordingly.
      </>
    ),
  },
  {
    title: "6. Data Retention",
    body: (
      <>
        We retain personal and booking information for as long as necessary
        to provide our services, comply with legal obligations, resolve
        disputes, and enforce our agreements. Memorial pages and recordings
         may be retained beyond an event date at the client&apos;s request.
      </>
    ),
  },
  {
    title: "7. Your Rights",
    body: (
      <>
        Depending on your location, you may have the right to access,
        correct, or request deletion of your personal information. See our{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          User Data Deletion
        </Box>{" "}
        page for how to submit a deletion request.
      </>
    ),
  },
  {
    title: "8. Security",
    body: (
      <>
        We use reasonable technical and organizational measures to protect
        your information. No method of transmission or storage is completely
        secure, and we cannot guarantee absolute security.
      </>
    ),
  },
  {
    title: "9. Changes to This Policy",
    body: (
      <>
        We may update this Privacy Policy from time to time. Changes take
        effect once posted on this page. We encourage you to review this
        page periodically.
      </>
    ),
  },
  {
    title: "10. Contact Us",
    body: (
      <>
        If you have questions about this Privacy Policy or wish to exercise
        your data rights, please reach out via our{" "}
        <Box
          component="span"
          sx={{ fontWeight: 700 }}
        >
          Contact
        </Box>{" "}
        page or your account representative.
      </>
    ),
  },
];

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: colors.primary[400], minHeight: "100vh" }}>
      <Box sx={{ maxWidth: "860px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 6, md: 10 } }}>
        <Box
          component="img"
          onClick={() => navigate("/")}
          src={muzukurulogo}
          alt="Muzukuru logo"
          sx={{ width: { xs: "140px", sm: "160px" }, height: "auto", cursor: "pointer", mb: 5 }}
        />

        <Typography
          sx={{
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            fontWeight: 800,
            color: colors.primary[900],
            mb: 1,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: `${colors.primary[900]}70`, mb: 5 }}>
          Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>

        <Stack divider={<Divider sx={{ borderColor: colors.primary[100] }} />} spacing={4}>
          {SECTIONS.map((section) => (
            <Box key={section.title}>
              <Typography
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 700,
                  color: colors.primary[900],
                  mb: 1.5,
                }}
              >
                {section.title}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: "0.9375rem",
                  lineHeight: 1.7,
                  color: `${colors.primary[900]}90`,
                }}
              >
                {section.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
